/**
 * Tintable accent per character illustration — which fill value in the
 * *colour* artwork a tint replaces, or `null` when the artwork has no accent.
 *
 * Pure data, measured off the committed assets rather than assumed. Across the
 * set, 74 artworks carry exactly one accent, two carry a dominant accent plus a
 * pale companion (only the dominant one is declared, so tinting keeps the
 * companion as authored), and one is ink-and-white only and therefore has
 * nothing to tint.
 *
 * Hand-declared data mirroring file content drifts silently, so accents.test.ts
 * asserts every entry against its asset using the same matcher the substitution
 * uses: a re-export that changes an accent fails the suite instead of degrading
 * into a substitution that matches nothing.
 */

import type { CharacterIllustrationName } from './names';

export const characterIllustrationAccents: Record<CharacterIllustrationName, string | null> = {
  'ai-conversation': '#fae355',
  'being-active': '#fcd3bf',
  breathalyser: '#c497a9',
  'care-team-at-work-emotional': '#a4bdff',
  'care-team-at-work-functional': '#a4bdff',
  'carrying-boat': '#c497a9',
  'character-a': '#a4bdff',
  'character-b': '#a4bdff',
  'character-c': '#a4bdff',
  'character-d': '#a4bdff',
  cyclist: '#fcd3bf',
  exploring: '#722f49',
  family: '#4d6525',
  'feeling-support': '#ddf1c4',
  fireworks: '#f9e8fe',
  'friends-celebration': '#a4bdff',
  'getting-started-beach': '#fae355',
  'getting-started-boat': '#fae355',
  'growth-lg': '#ebf7dc',
  'growth-sm': '#ebf7dc',
  guidance: '#fae355',
  'helping-lg': '#4d6525',
  'helping-sm': '#4d6525',
  'high-five': '#fcd3bf',
  'home-delivery': '#722f49',
  'how-to-use-it': '#a4bdff',
  'human-emotions': '#722f49',
  'ice-cream-fail': '#fcd3bf',
  idea: '#fae355',
  juggle: '#fcd3bf',
  'living-room': '#4d6525',
  'looking-ahead-lg': '#4d6525',
  'looking-ahead-sm': '#4d6525',
  'man-thumbs-up': '#722f49',
  'man-waving': '#fae355',
  map: '#ddf1c4',
  meditation: '#ebf7dc',
  'member-taking-medication-alcohol': '#ddf1c4',
  'member-taking-medication-tobacco': '#ddf1c4',
  'member-taking-urine-test': '#fcd3bf',
  'member-using-app': '#fae355', // dominant of two; pale #e8eeff companion left as authored
  'member-using-app-blue': '#a4bdff', // dominant of two; pale #e8eeff companion left as authored
  'mindfulness-activities-relaxing': '#4d6525',
  'nature-landscape': '#4d6525',
  'nature-relaxation': '#fae355',
  'negative-thoughts': '#722f49',
  notifications: '#fae355',
  'office-space': '#c497a9',
  'overcoming-obstacles': '#4d6525',
  'peaceful-butterfly': '#fcd3bf',
  'picking-fruit': '#722f49',
  progress: '#c497a9',
  'progress-wide': '#c497a9',
  'reading-book': '#722f49',
  'reading-card': '#fae355',
  'reading-map': '#a4bdff',
  relapse: '#fcd3bf',
  'relationship-interaction': '#fae355',
  'relaxing-together': null,
  rowing: '#4d6525',
  'sailing-together-lg': '#fcd3bf',
  'sailing-together-sm': '#fcd3bf',
  'save-money': '#c497a9',
  'setting-goals-archery': '#722f49',
  'setting-goals-calendar': '#722f49',
  skipping: '#ddf1c4',
  success: '#fae355',
  thinking: '#722f49',
  'waking-up': '#fae355',
  walking: '#fae355',
  'walking-bridge-lg': '#fcd3bf',
  'walking-bridge-sm': '#fcd3bf',
  'watering-plant': '#4d6525',
  welcome: '#a4bdff',
  'well-done': '#fae355',
  'woman-thumbs-up': '#ddf1c4',
  'woman-thumbs-up-2': '#a4bdff',
};

/**
 * The accent a tint replaces for this illustration, or `null` when it has none.
 *
 * Normalises an unregistered name to `null` rather than letting `undefined`
 * escape: the type forbids one, but the callers that matter exist precisely to
 * survive a value that bypassed validation, and a bare index would hand them
 * `undefined` to mistake for "has an accent".
 */
export function getCharacterIllustrationAccent(name: CharacterIllustrationName): string | null {
  return characterIllustrationAccents[name] ?? null;
}
